/**
 * GuestHistoryPanel — the chat-history rail for a guest (SCRUM-273: "no
 * history list — 'Sign in to save your chats'").
 *
 * DRAFTED STATE (GUARDRAILS rule 10): the prototype has no guest rail. This
 * keeps ThreadList's anatomy — the 256px rail, the same «Новый чат» control,
 * the same mobile sheet, the imported design's dark rail at ≥1200px light —
 * so the surface does not jump between guest and member, and replaces the
 * list with a locked panel whose one action opens the sign-up sheet. It never
 * calls `useThreadList`: GET /chat/threads is member-only, and a guest token
 * there only earns a 403 (contract: `auth.registration_required`).
 */
import { Lock, PanelLeftClose, Plus } from "lucide-react";
import { useEffect, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { DESIGN_BUTTON_CLASS } from "@/features/chat/ThreadList";
import { useAssistantDesign } from "@/features/chat/useDesignFrame";
import { iconProps } from "@/lib/icons";

export interface GuestHistoryPanelProps {
  onNewChat: () => void;
  /** Open the sign-up sheet (reason "history"). */
  onSignIn: () => void;
  /** Mobile sheet state — owned by ChatRoute, whose header holds the trigger. */
  mobileOpen: boolean;
  onMobileOpenChange: (open: boolean) => void;
}

/**
 * The mobile sheet's own `aria-labelledby` target (fix round 1). The desktop
 * rail and the mobile sheet both render `renderBody()` below, and the
 * desktop copy is always in the DOM (merely `hidden` under `md`) — reusing
 * ONE literal id for the title in both would duplicate it whenever the sheet
 * is open, and `aria-labelledby` would then risk resolving to the HIDDEN
 * (so accessible-name-empty) desktop copy instead of the visible one. Giving
 * the mobile title its own id keeps the reference unambiguous.
 */
const MOBILE_HISTORY_TITLE_ID = "chat-guest-history-sheet-title";

export function GuestHistoryPanel({
  onNewChat,
  onSignIn,
  mobileOpen,
  onMobileOpenChange,
}: GuestHistoryPanelProps): ReactElement {
  const { t } = useTranslation("chat");
  const design = useAssistantDesign();

  // Escape closes the mobile sheet (ThreadList's sheet predates this rule) —
  // UNLESS a sheet stacked above it already consumed the key (fix round 1):
  // W2.5's sign-up gate can open while this sheet is still open underneath,
  // and without this guard its own Escape handler and this one would both
  // fire, closing the history sheet out from under the gate the user meant
  // to dismiss.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && !event.defaultPrevented) {
        onMobileOpenChange(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileOpen, onMobileOpenChange]);

  // «Новый чат» closes the mobile sheet first, like ThreadList's own (final
  // web fix round, item 5) — the fresh chat is what the guest lands on, not
  // the sheet still covering it. Harmless on the desktop rail.
  const handleNewChat = (): void => {
    onMobileOpenChange(false);
    onNewChat();
  };

  /**
   * Shared between the desktop rail and the mobile sheet. `titleId`, when
   * given, is attached to the title paragraph so the mobile dialog's
   * `aria-labelledby` has something unambiguous to point at (see
   * `MOBILE_HISTORY_TITLE_ID` above) — the desktop rail passes none, since a
   * permanent sidebar is not a dialog and needs no such reference.
   */
  const renderBody = (titleId?: string): ReactElement => (
    <>
      <div className={design ? "" : "p-3"}>
        {design ? (
          <button
            type="button"
            className={DESIGN_BUTTON_CLASS}
            onClick={handleNewChat}
            data-testid="chat-new"
          >
            <Plus className="size-4" aria-hidden="true" />
            {t("newChat")}
          </button>
        ) : (
          <Button
            type="button"
            variant="secondary"
            className="h-11 w-full justify-start gap-2"
            onClick={handleNewChat}
            data-testid="chat-new"
          >
            <Plus className="size-4" aria-hidden="true" />
            {t("newChat")}
          </Button>
        )}
      </div>

      <div
        className={"flex flex-col items-start gap-2.5 py-4 " + (design ? "" : "px-4")}
        data-testid="chat-guest-history-locked"
      >
        <span
          aria-hidden="true"
          className={design ? "text-[var(--gd-panel-ink-3)]" : "text-[var(--g-ink-2)]"}
        >
          <Lock {...iconProps("lg")} />
        </span>
        <p
          id={titleId}
          className={
            "text-[15px] font-semibold leading-snug " +
            (design ? "text-[var(--gd-panel-ink)]" : "text-foreground")
          }
        >
          {t("guest.history.title")}
        </p>
        <p
          className={
            "text-[14px] leading-[1.5] " +
            (design ? "text-[var(--gd-panel-ink-3)]" : "text-[var(--g-ink-2)]")
          }
        >
          {t("guest.history.body")}
        </p>
        {design ? (
          <button
            type="button"
            onClick={onSignIn}
            aria-haspopup="dialog"
            data-testid="chat-guest-history-signin"
            className={DESIGN_BUTTON_CLASS}
          >
            {t("guest.history.cta")}
          </button>
        ) : (
          // The design system's own primary CTA (fix round 1) — the same
          // bare `<Button>` + `h-11` convention `PhoneVerifyDialog` uses for
          // its primary actions, rather than a one-off hand-rolled class.
          <Button
            type="button"
            onClick={onSignIn}
            aria-haspopup="dialog"
            data-testid="chat-guest-history-signin"
            className="h-11"
          >
            {t("guest.history.cta")}
          </Button>
        )}
      </div>
    </>
  );

  return (
    <>
      <aside
        className={
          "hidden w-64 shrink-0 flex-col md:flex " +
          (design
            ? "gap-5 bg-[var(--gd-panel)] px-4 py-5"
            : "border-r border-[var(--g-rule)] bg-[var(--g-surface)]")
        }
        style={design ? { fontFamily: "var(--gd-text)" } : undefined}
        aria-label={t("guest.locked.historyAria")}
        data-testid="chat-guest-history"
        data-design={design ? "true" : undefined}
      >
        {renderBody()}
      </aside>

      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onMobileOpenChange(false);
          }}
          data-testid="chat-guest-history-sheet-overlay"
        >
          <aside
            role="dialog"
            aria-modal="true"
            aria-labelledby={MOBILE_HISTORY_TITLE_ID}
            className="flex h-full w-72 max-w-[85vw] flex-col border-r border-border bg-card"
            data-testid="chat-guest-history-sheet"
          >
            <div className="flex justify-end p-1">
              <button
                type="button"
                aria-label={t("sidebar.close")}
                onClick={() => onMobileOpenChange(false)}
                data-testid="chat-guest-history-close"
                className="flex size-11 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
              >
                <PanelLeftClose className="size-4" aria-hidden="true" />
              </button>
            </div>
            {renderBody(MOBILE_HISTORY_TITLE_ID)}
          </aside>
        </div>
      )}
    </>
  );
}

export default GuestHistoryPanel;
